import { iconEnumToString } from "@storytell/bsky-icons";
import {
  type AssociateLabelInput,
  AssociateLabelRequestConfig,
  CreateLabelRequestConfig,
  type DisassociateLabelInput,
  DisassociateLabelRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { Icons } from "@storytell/cp-client";
import type { AssetId, Label, LabelAssociation, LabelId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbCircleCheck } from "solid-icons/tb";
import { type Component, batch, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { LabelPicker } from "./LabelPicker";

export const BulkAssetLabelPicker: Component<{
  mount?: Node;
  selected: AssetId[];
  onClose: () => void;
  onLabelCreated: (label: Label) => void;
}> = (props) => {
  const wire = useWire();
  const [loading, setLoading] = createSignal(false);
  const storytellClient = useStorytellClient();

  const labelsInCommon = () => {
    let workingSet: Set<string> | undefined;
    for (const id of props.selected) {
      const labels = wire.rels.labelsForAsset(id);
      if (!workingSet) {
        workingSet = new Set(labels);
        continue;
      }
      workingSet = workingSet.intersection(new Set(labels));
    }
    return Array.from(workingSet ?? []);
  };

  const partialLabels = () => {
    const labelCounts = new Map<string, number>();
    for (const id of props.selected) {
      const labels = wire.rels.labelsForAsset(id);
      for (const labelId of labels) {
        labelCounts.set(labelId, (labelCounts.get(labelId) || 0) + 1);
      }
    }
    // Return labels that are present on some but not all assets
    return Array.from(labelCounts.entries())
      .filter(([_, count]) => count > 0 && count < props.selected.length)
      .map(([labelId]) => labelId);
  };

  const [search, setSearch] = createSignal("");
  const [labels, setLabels] = createSignal<string[]>(labelsInCommon());
  const [explicitlyChanged, setExplicitlyChanged] = createSignal<Record<string, boolean>>({});

  // For display purposes, filter out partial labels that have been explicitly changed
  const displayPartialLabels = () => {
    const partial = partialLabels();
    const changed = explicitlyChanged();
    return partial.filter((labelId) => !changed[labelId]);
  };

  const onApply = async () => {
    setLoading(true);
    try {
      const explicitlyChangedLabels = Object.entries(explicitlyChanged())
        .filter(([_, value]) => value)
        .map(([id]) => id);
      const labelsToAdd = new Set(explicitlyChangedLabels.filter((l) => labels().includes(l)));
      const labelsToRemove = new Set(explicitlyChangedLabels.filter((l) => !labels().includes(l)));
      const ops = [];

      if (labelsToAdd.size > 0) {
        const associateRequests: AssociateLabelInput[] = [];
        for (const labelId of labelsToAdd) {
          for (const assetId of props.selected) {
            associateRequests.push({
              labelId,
              resourceId: assetId,
              resourceType: "asset",
            });
          }
        }
        const requestConfig = AssociateLabelRequestConfig.withBearerToken(wire.auth.token());
        ops.push(storytellClient.fetch(requestConfig, undefined, associateRequests));
      }

      if (labelsToRemove.size > 0) {
        const disassociateRequests: DisassociateLabelInput[] = [];
        for (const labelId of labelsToRemove) {
          for (const assetId of props.selected) {
            const associationId = wire.labels.getAssociation(labelId, assetId)?.id;
            if (!associationId) continue;
            disassociateRequests.push({ associationId });
          }
        }
        const requestConfig = DisassociateLabelRequestConfig.withBearerToken(wire.auth.token());
        ops.push(storytellClient.fetch(requestConfig, undefined, disassociateRequests));
      }

      await Promise.all(ops);

      batch(() => {
        for (const id of props.selected) {
          for (const labelId of explicitlyChangedLabels) {
            if (labelsToAdd.has(labelId)) {
              wire.rels.addLabelToAsset(id, labelId as LabelId);
            } else {
              wire.rels.removeLabelFromAsset(id, labelId as LabelId);
            }
          }
        }
      });

      wire.labels.refetchLabelAssociations();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      props.onClose();
    }
  };

  const onCreateLabel = async (data: {
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }) => {
    try {
      const client = useStorytellClient();
      const request = CreateLabelRequestConfig.withBearerToken(wire.auth.token());
      const result = await client.fetch(request, undefined, {
        name: data.name,
        description: data.description,
        icon: iconEnumToString(data.icon),
        color: data.color,
      });

      if (result.isOk()) {
        const label = result.value;
        wire.labels.set(label.id as LabelId, { ...label, userId: wire.auth.user()?.id || "" });
        const associateRequests: AssociateLabelInput[] = props.selected.map((assetId) => ({
          labelId: label.id,
          resourceId: assetId,
          resourceType: "asset",
        }));
        const requestConfig = AssociateLabelRequestConfig.withBearerToken(wire.auth.token());
        const res = await storytellClient.fetch(requestConfig, undefined, associateRequests);
        if (res.isOk()) {
          res.value.forEach((assoc) => wire.labels.onLabelAssociated(assoc as LabelAssociation));
        }
        props.onLabelCreated(label);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <LabelPicker
        mount={props.mount}
        search={search()}
        setSearch={setSearch}
        labels={wire.labels.search(search())}
        selectedLabels={labels()}
        partialLabels={displayPartialLabels()}
        title={`Change labels for ${props.selected.length} asset${props.selected.length === 1 ? "" : "s"}`}
        onSelectLabel={(l) => {
          setLabels((prev) => [...prev, l.id]);
          setExplicitlyChanged((prev) => ({ ...prev, [l.id]: true }));
        }}
        onDeselectLabel={(l) => {
          setLabels((prev) => prev.filter((id) => id !== l.id));
          setExplicitlyChanged((prev) => ({ ...prev, [l.id]: true }));
        }}
        onCreateLabel={onCreateLabel}
      />

      <StButton
        disabled={loading()}
        loading={loading()}
        icon={TbCircleCheck}
        noColor
        class="mb-3 ml-auto bg-brand hover:bg-brand-hover text-on-brand"
        onClick={onApply}
      >
        Apply changes
      </StButton>
    </>
  );
};
